import { useCallback } from "react";

import { useTimezone } from "./use-time";

/** The product's default date + time rendering options (used when none given). */
export const DEFAULT_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

/**
 * Format a date/time in an explicit IANA zone. Returns `""` for an unparseable
 * value rather than throwing.
 */
export function formatInTimeZone(
  value: Date | number | string,
  timeZone: string,
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale, {
    ...(options ?? DEFAULT_DATE_TIME_OPTIONS),
    timeZone,
  }).format(date);
}

/**
 * A formatter bound to the active `TimeProvider` zone. Components should prefer
 * this over `toLocaleString()` so every timestamp follows the user's choice.
 */
export function useTimeFormatter() {
  const { timeZone } = useTimezone();
  return useCallback(
    (
      value: Date | number | string,
      options?: Intl.DateTimeFormatOptions,
      locale?: string,
    ) => formatInTimeZone(value, timeZone, options, locale),
    [timeZone],
  );
}
